/** The town screen's LIFE: what moves, where, and how it is posed at a time.
 *
 * A classic town screen is never still - flags wave on the keep, smoke
 * rises from the smithy, clouds cross the sky, birds cross the clouds, water
 * glints and lava breathes. This module is the whole of that, as data: a plan
 * of items per faction (`townLife`) and a pure pose function (`poseLife`)
 * that turns the plan and a clock reading into frames and positions.
 *
 * Everything is a function of ONE number, the presenter clock in
 * milliseconds. There is no per-item state, no easing and no randomness, so
 * two screens at the same clock reading draw the same frame - and a harness
 * that holds the clock at zero (reduced motion, `disableAnimations`) settles
 * to the same still every time. The animation itself is hand-stepped, the way
 * a 1999 town screen's was: a flag has four states and holds each one.
 *
 * WHERE things live is the painting's business, not a rule's: each faction's
 * layout (townLayouts.ts, read off its own backdrop) says where its water
 * glints, where its lava glows, where its ground fog lies and where its sky
 * is, so the life follows the composition - the harbour's bay, the cavern's
 * lake, the marsh's pools - rather than three rows every faction shared.
 *
 * Pure: no DOM, no React, no kernel rule module.
 */

import type { ResolvedSlot, TownSlot } from "./townSlots.js";
import { TOWN_CANVAS_H, TOWN_CANVAS_W, TOWN_HD, bandScale, drawnWidth, skyBand } from "./townSlots.js";
import { TOWN_ANCHORS } from "./townAnchors.js";
import { TOWN_LAYOUTS } from "./townLayouts.js";
import { hash01 } from "./hash01.js";

/** One strip in TOWNS/assets/life, keyed `town.life.<name>_<frame>`. */
export interface LifeSprite {
  readonly frames: number;
  readonly fps: number;
  /** Canvas-unit size; the frame file is TOWN_HD times this (gen_life.py). */
  readonly w: number;
  readonly h: number;
}

/**
 * The strips gen_life.py draws. Mirrors `TOWNS/assets/life/life.json`; a test
 * holds the two together, because a frame count wrong here is a flag that
 * shows a blank quarter of the time.
 */
export const LIFE_SPRITES: Readonly<Record<string, LifeSprite>> = Object.freeze({
  bird: { frames: 4, fps: 8, w: 14, h: 10 },
  bird_pale: { frames: 4, fps: 8, w: 14, h: 10 },
  bat: { frames: 4, fps: 10, w: 14, h: 10 },
  cloud_a: { frames: 1, fps: 0, w: 150, h: 44 },
  cloud_b: { frames: 1, fps: 0, w: 104, h: 34 },
  cloud_c: { frames: 1, fps: 0, w: 72, h: 26 },
  cloud_dark_a: { frames: 1, fps: 0, w: 150, h: 44 },
  cloud_dark_b: { frames: 1, fps: 0, w: 104, h: 34 },
  cloudbank: { frames: 1, fps: 0, w: 320, h: 92 },
  ripple: { frames: 4, fps: 3, w: 26, h: 8 },
  ripple_dark: { frames: 4, fps: 3, w: 26, h: 8 },
  ripple_violet: { frames: 4, fps: 3, w: 26, h: 8 },
  // the swell that travels along a run of open water (gen_life.sheen_frame)
  sheen: { frames: 4, fps: 2, w: 140, h: 16 },
  sheen_dark: { frames: 4, fps: 2, w: 140, h: 16 },
  sheen_violet: { frames: 4, fps: 2, w: 140, h: 16 },
  fog_a: { frames: 1, fps: 0, w: 220, h: 28 },
  fog_b: { frames: 1, fps: 0, w: 160, h: 24 },
  ember: { frames: 3, fps: 4, w: 8, h: 8 },
  glint: { frames: 3, fps: 4, w: 8, h: 8 },
  glint_violet: { frames: 3, fps: 4, w: 8, h: 8 },
  puff: { frames: 4, fps: 2, w: 18, h: 18 },
  ...Object.fromEntries(
    ["sunspire", "greenmantle", "skyreach", "emberpit", "gravewatch", "deepvault", "ironhorde",
      "fenmire", "prismgate", "saltreach", "cogworks", "frostmark", "blackwater"]
      .flatMap((f) => [
        [`flag_${f}`, { frames: 4, fps: 6, w: 22, h: 14 }],
        [`pennant_${f}`, { frames: 4, fps: 5, w: 15, h: 10 }],
      ]),
  ),
});

/** The life frames ship at this many times their canvas size (gen_life.HD). */
export const LIFE_HD = TOWN_HD;

export const lifeKey = (sprite: string, frame: number): string =>
  `town.life.${sprite}_${String(frame)}`;

/**
 * Where an item is drawn relative to the buildings.
 *
 *  - `sky`: behind every building - clouds, birds, the glints on far water.
 *  - `over`: in front of every building - flags, smoke, embers, fog.
 */
export type LifeLayer = "sky" | "over";

export interface LifeItem {
  readonly sprite: string;
  readonly layer: LifeLayer;
  /** Top-left of the frame at phase zero, canvas pixels. */
  readonly x: number;
  readonly y: number;
  /** Magnification of the canvas-unit size; fractional is fine on hi-def art. */
  readonly scale: number;
  /** A time offset in ms, so five glints do not blink in unison. */
  readonly phase: number;
  readonly opacity: number;
  /**
   * Steady drift, px per second, wrapping every `loop` px of travel: clouds,
   * birds, fog. The wrap is on travel rather than on the canvas edge so an
   * item re-enters from the side it left.
   */
  readonly drift?: { readonly vx: number; readonly vy: number; readonly loop: number };
  /**
   * A cycle of `life` ms in which the item rises `rise` px, steps through its
   * frames once and fades out over the last third: a puff of smoke, an ember.
   */
  readonly cycle?: { readonly life: number; readonly rise: number; readonly sway: number };
  /** Shown only for the first `duty` of each frame cycle: a glint blinks. */
  readonly duty?: number;
  /** Flip the frame horizontally (a flag flying left). */
  readonly flip?: boolean;
}

/**
 * A soft light on the painting that breathes: lava in the fissures, the
 * crystals of the cavern, the aurora over the isle. A radial gradient rather
 * than a sprite, because what glows is the painted ground and a light on it
 * is a wash, not an object.
 */
export interface LifeGlow {
  readonly x: number;
  readonly y: number;
  readonly rx: number;
  readonly ry: number;
  readonly rgb: string;
  /** Opacity at rest and how far it swings, plus the period in ms. */
  readonly alpha: number;
  readonly swing: number;
  readonly period: number;
  readonly phase: number;
}

export interface LifePlan {
  readonly items: readonly LifeItem[];
  readonly glows: readonly LifeGlow[];
}

export interface LifePose {
  readonly item: LifeItem;
  readonly key: string;
  readonly x: number;
  readonly y: number;
  readonly opacity: number;
}

// ------------------------------------------------------------------ posing

/** Which frame of an `fps` strip of `frames` is up at `t` ms. */
export function lifeFrame(t: number, sprite: LifeSprite, phase = 0): number {
  if (sprite.frames <= 1 || sprite.fps <= 0) return 0;
  const step = Math.floor(((Math.max(0, t) + phase) / 1000) * sprite.fps);
  return ((step % sprite.frames) + sprite.frames) % sprite.frames;
}

/** Deterministic 0..1 from an integer seed. */

/**
 * The clock an item sees: its phase is added only once the clock is RUNNING.
 * At t=0 every item is at its plan position on frame 0 whatever its phase,
 * so a harness that holds the clock at zero photographs the plan itself.
 */
const localClock = (t: number, phase: number): number => (t <= 0 ? 0 : t + phase);

/**
 * Pose every item at clock `t`.
 *
 * Nothing here is stateful: a drifting cloud's position is `x0 + v*t` wrapped,
 * a puff's age is `t mod life`, a glint's blink is `t mod period`. At t=0 the
 * pose is the plan itself.
 */
export function poseLife(plan: LifePlan, t: number): readonly LifePose[] {
  const out: LifePose[] = [];
  for (const item of plan.items) {
    const sprite = LIFE_SPRITES[item.sprite];
    if (sprite === undefined) continue;
    let x = item.x;
    let y = item.y;
    let opacity = item.opacity;
    const local = localClock(t, item.phase);
    let frame = lifeFrame(local, sprite, 0);
    if (item.drift !== undefined) {
      const travel = (local / 1000) * item.drift.vx;
      const loop = item.drift.loop;
      // Travel wraps on the loop; the start of the loop is the plan's own x.
      const along = ((travel % loop) + loop) % loop;
      x = item.x + (item.drift.vx >= 0 ? along : -along);
      // A drifting thing that leaves the far side re-enters from the near one.
      const w = sprite.w * item.scale;
      if (x > TOWN_CANVAS_W) x -= loop;
      if (x + w < 0) x += loop;
      if (item.drift.vy !== 0) {
        // a bird's flight bobs; a cloud's does not
        y = item.y + Math.sin((local / 1000) * 0.9) * 4;
      }
    }
    if (item.cycle !== undefined) {
      const age = ((local % item.cycle.life) + item.cycle.life) % item.cycle.life;
      const k = age / item.cycle.life;
      y = item.y - item.cycle.rise * k;
      x = item.x + Math.sin(k * Math.PI * 2) * item.cycle.sway + item.cycle.sway * k;
      frame = Math.min(sprite.frames - 1, Math.floor(k * sprite.frames));
      // fade over the last third, in three flat steps
      opacity = k < 0.66 ? item.opacity : k < 0.82 ? item.opacity * 0.66 : item.opacity * 0.33;
    }
    if (item.duty !== undefined) {
      const period = (sprite.frames / Math.max(1, sprite.fps)) * 1000 * 2;
      const at = ((local % period) + period) % period;
      if (at > period * item.duty) continue;
      frame = Math.min(sprite.frames - 1, Math.floor((at / (period * item.duty)) * sprite.frames));
    }
    out.push({ item, key: lifeKey(item.sprite, frame), x: Math.round(x), y: Math.round(y), opacity });
  }
  return out;
}

/** A glow's opacity at `t`: a slow breath, held at rest at t=0. */
export function glowAlpha(glow: LifeGlow, t: number): number {
  const local = localClock(t, glow.phase);
  // Stepped, not smooth: eight levels per period, so the light breathes the
  // way a hand-animated palette cycle does.
  const k = Math.floor((((local % glow.period) + glow.period) % glow.period) / glow.period * 8) / 8;
  return glow.alpha + glow.swing * Math.sin(k * Math.PI * 2);
}

// ------------------------------------------------------------------ the plan

const DAYLIGHT: ReadonlySet<string> = new Set([
  "sunspire", "greenmantle", "skyreach", "ironhorde", "prismgate", "saltreach", "cogworks", "frostmark",
]);
const DUSK: ReadonlySet<string> = new Set(["gravewatch", "emberpit", "fenmire", "blackwater"]);
/** Bats rather than birds, and no clouds where there is no sky. */
const UNDERGROUND: ReadonlySet<string> = new Set(["deepvault"]);
/** Where the water is a lit sea rather than a river: more glints, bigger. */
const SEA: ReadonlySet<string> = new Set(["saltreach"]);
/**
 * Towns whose bottom edge is air rather than ground.
 *
 * Prismgate is a floating isle seen from a little above its front rim and
 * Tower and Frostmark are peaks above a cloud layer: the frame's lower band is
 * sky in all three, so a bank of cloud drifts across it in front of
 * everything, which is both what the place would look like and the fix for a
 * measured dead zone.
 */
const FOREGROUND_CLOUD: ReadonlySet<string> = new Set(["prismgate"]);

/** What the faction's layout says about its painting; empty when it has none. */
function scenery(faction: string) {
  const own = TOWN_LAYOUTS[faction];
  return {
    water: own?.water ?? [],
    waterRuns: own?.waterRuns ?? [],
    lava: own?.lava ?? [],
    fog: own?.fog ?? [],
    glow: own?.glow ?? [],
  };
}

function clouds(faction: string, dark: boolean, sky: readonly [number, number]): LifeItem[] {
  const names = dark ? ["cloud_dark_a", "cloud_dark_b", "cloud_dark_a"] : ["cloud_a", "cloud_b", "cloud_c"];
  const seed = faction.length * 131 + faction.charCodeAt(0) * 7;
  const [top, bottom] = sky;
  return names.map((sprite, i) => {
    const s = LIFE_SPRITES[sprite]!;
    const room = Math.max(0, bottom - top - s.h);
    return {
      sprite, layer: "sky" as const,
      x: Math.round(hash01(seed + i) * TOWN_CANVAS_W),
      y: Math.round(top + hash01(seed + 10 + i) * room),
      scale: 1, phase: 0, opacity: dark ? 0.55 : 0.8,
      // The far cloud is the slow one: 4 to 9 px/s, a crossing in three to
      // five minutes, which is the pace of weather rather than of a sprite.
      drift: { vx: 4 + i * 2.5, vy: 0, loop: TOWN_CANVAS_W + s.w * 2 },
    };
  });
}

function flock(sprite: string, x: number, y: number, vx: number, scale: number): LifeItem[] {
  const offs: readonly (readonly [number, number])[] = [[0, 0], [-16, 7], [-30, 3], [14, 9]];
  return offs.map(([dx, dy], i) => ({
    sprite, layer: "sky" as const, x: x + dx * scale, y: y + dy * scale, scale,
    phase: i * 90, opacity: 1,
    drift: { vx, vy: 1, loop: TOWN_CANVAS_W + 400 },
  }));
}

/** The tip of a building's art and its chimney, from the generated table. */
function anchorOf(r: ResolvedSlot): { tip: number; ratio: number; chimney: readonly [number, number] | null } | null {
  if (r.built === null) return null;
  return TOWN_ANCHORS[r.built.id as string] ?? null;
}

function topLeft(slot: TownSlot, ratio: number): { left: number; top: number; w: number } {
  const w = drawnWidth(slot);
  return { left: slot.x - w / 2, top: slot.y - w * ratio, w };
}

/**
 * The life of one town.
 *
 * `painted` is the resolved slot list with a building in it; the plan is a
 * function of what is BUILT (no flag on a keep that is not there yet), which
 * is also why it is recomputed when the town changes and not per frame.
 */
export function townLife(faction: string, painted: readonly ResolvedSlot[]): LifePlan {
  const items: LifeItem[] = [];
  const glows: LifeGlow[] = [];
  const dark = DUSK.has(faction);
  const underground = UNDERGROUND.has(faction);
  const sky = skyBand(faction);
  const scene = scenery(faction);

  // --- the sky: clouds where there is one, birds by day, bats by night
  if (!underground) items.push(...clouds(faction, dark, sky));
  const flockY = Math.round(sky[0] + (sky[1] - sky[0]) * 0.45);
  if (DAYLIGHT.has(faction)) {
    const pale = faction === "skyreach" || faction === "frostmark";
    items.push(...flock(pale ? "bird_pale" : "bird", -60, flockY, 22, 1));
    items.push(...flock("bird", 700, Math.min(sky[1] - 12, flockY + 22), 15, 0.8));
  } else if (faction === "gravewatch" || underground) {
    items.push(...flock("bat", 200, flockY, 26, 1));
  }

  // --- ground fog where the painting has a marsh, a delta, a dusty ravine
  for (const [i, [x, y, w]] of scene.fog.entries()) {
    const sprite = i % 2 === 0 ? "fog_a" : "fog_b";
    const s = LIFE_SPRITES[sprite]!;
    items.push({
      sprite, layer: "over", x, y: Math.round(y - s.h / 2), scale: w / s.w, phase: i * 700, opacity: 0.45,
      drift: { vx: 3 + i, vy: 0, loop: TOWN_CANVAS_W + w },
    });
  }

  // --- water: the painted water SHIMMERS rather than sparkling in six places.
  //
  // The blind critic measured our motion against a real the expansion town screen over
  // a matched 600 ms window: ours moved 0.68-3.12% of the frame, the expansion's Saltreach
  // 9.25%, "and the Castle river and the whole Saltreach bay - the largest water
  // bodies in each frame - are completely static". A glint is a spark; what
  // reads as water is a lit crest travelling along it. So every point the
  // layout marks as water carries a cluster: three glints on their own
  // phases and two ripples drifting sideways along the surface.
  const sea = SEA.has(faction);
  const violet = underground || faction === "prismgate";
  const rippleSprite = violet ? "ripple_violet" : dark ? "ripple_dark" : "ripple";
  for (const [i, [x, y]] of scene.water.entries()) {
    const g = LIFE_SPRITES["glint"]!;
    const k = sea ? 1.4 : 1;
    for (let j = 0; j < 3; j++) {
      const dx = ([0, -14, 13][j] ?? 0) * k;
      const dy = ([0, 5, -4][j] ?? 0) * k;
      items.push({
        sprite: violet ? "glint_violet" : "glint",
        layer: "sky",
        x: Math.round(x + dx - (g.w * k) / 2), y: Math.round(y + dy - (g.h * k) / 2),
        scale: k * (j === 0 ? 1 : 0.75),
        phase: i * 530 + j * 310, opacity: 0.95, duty: 0.4,
      });
    }
    // Three crests a point, half as wide again as the sprite, travelling
    // fast enough to be seen to travel: the measured change over 600 ms on
    // the Saltreach bay is what the critic weighed against the expansion's own harbour.
    const r = LIFE_SPRITES[rippleSprite]!;
    for (let j = 0; j < 3; j++) {
      const rk = k * (1.15 + 0.42 * j);
      items.push({
        sprite: rippleSprite, layer: "sky",
        x: Math.round(x - (r.w * rk) / 2 + ([-26, 18, -4][j] ?? 0)),
        y: Math.round(y - (r.h * rk) / 2 + ([9, -7, 18][j] ?? 0)),
        scale: rk, phase: i * 370 + j * 520, opacity: sea ? 0.7 : 0.55,
        drift: { vx: [9, -7, 6][j] ?? 6, vy: 0, loop: 72 },
      });
    }
  }

  // --- ...and the SWELL that carries the whole surface.
  //
  // Crests on points animate the places the layout named; what the critic
  // measured as missing was the water BETWEEN them - "the Castle river and
  // the whole Saltreach bay, the largest water bodies in each frame, completely
  // static". `waterRuns` is the span between two declared water points
  // (town_layout.water_runs), and a run carries one long soft swell per
  // 210 canvas px of its length, travelling along it and breathing as it
  // goes. They are on the SKY layer, behind every building, so a swell that
  // passes a quay is occluded by the quay exactly as the water is.
  const sheenSprite = violet ? "sheen_violet" : dark ? "sheen_dark" : "sheen";
  const sh = LIFE_SPRITES[sheenSprite]!;
  for (const [i, [x0, x1, y]] of scene.waterRuns.entries()) {
    const span = x1 - x0;
    const n = Math.max(1, Math.round(span / 210));
    for (let j = 0; j < n; j++) {
      // A swell is drawn a little wider than the cell it travels in, so a
      // run reads as one moving surface and not as a row of lozenges.
      const k = (span / n / sh.w) * (sea ? 1.25 : 1.05);
      const at = x0 + (span / n) * j;
      items.push({
        sprite: sheenSprite, layer: "sky",
        x: Math.round(at), y: Math.round(y - (sh.h * k) / 2),
        scale: k,
        phase: i * 640 + j * 990,
        opacity: sea ? 0.5 : 0.4,
        // slow: a swell crosses its own cell in twenty to forty seconds,
        // which is the pace of water rather than of a sprite
        drift: { vx: (j % 2 === 0 ? 1 : -1) * (5 + (i % 3) * 2), vy: 0, loop: span / n },
      });
    }
  }

  // --- fire: embers off the lava and a breathing glow on it
  for (const [i, [x, y]] of scene.lava.entries()) {
    items.push({
      sprite: "ember", layer: "over", x, y, scale: 1, phase: i * 410, opacity: 0.9,
      cycle: { life: 2600 + i * 180, rise: 46, sway: 5 },
    });
  }
  for (const [i, [x, y, rx, ry, rgb]] of scene.glow.entries()) {
    const big = rx >= 200;
    glows.push({
      x, y, rx, ry, rgb, alpha: big ? 0.14 : 0.24, swing: big ? 0.08 : 0.12,
      period: big ? 5000 : 2400 + i * 300, phase: i * 300,
    });
  }

  // --- the buildings: flags on the keep, the hall and the grail, smoke from
  // the smithy and the tavern.
  for (const [bi, r] of painted.entries()) {
    const a = anchorOf(r);
    if (a === null) continue;
    const box = topLeft(r.slot, a.ratio);
    const name = r.slot.name;
    const k = Math.max(0.7, Math.min(1.4, bandScale(r.slot)));
    const big = name === "fort" || name === "hall" || name === "grail";
    // EVERY roof flies something. One flag on the keep and nothing else
    // moving was the second half of the motion measurement: the classic flies a
    // banner over most of its dwellings and all of its civic buildings.
    // The keep, the hall and the grail take the full flag; everything else
    // takes the smaller pennant, at the band's own scale, so a back-shelf
    // dwelling gets a fleck of colour and a front one a real banner.
    const flagName = LIFE_SPRITES[`flag_${faction}`] === undefined ? "sunspire" : faction;
    const sprite = big ? `flag_${flagName}` : `pennant_${flagName}`;
    const fs = LIFE_SPRITES[sprite]!;
    // a pennant on a small far building is noise: below this it is left off
    if (big || box.w * k >= 70) {
      items.push({
        sprite, layer: "over",
        x: Math.round(box.left + a.tip * box.w),
        y: Math.round(box.top - fs.h * k + 2),
        scale: k,
        phase: big ? (name === "hall" ? 250 : name === "grail" ? 500 : 0) : 130 * (bi % 7),
        opacity: big ? 1 : 0.95,
      });
    }
    // Smoke off every chimney the art has, not only the smithy's and the
    // tavern's: a town where two roofs smoke and fifteen do not is a town
    // where nobody is home.
    if (a.chimney !== null) {
      const hot = name === "blacksmith" || name === "tavern";
      const cx = box.left + a.chimney[0] * box.w;
      const cy = box.top + a.chimney[1] * box.w * a.ratio;
      const puffs = hot ? 3 : 2;
      for (let i = 0; i < puffs; i++) {
        items.push({
          sprite: "puff", layer: "over",
          x: Math.round(cx - 9 * k), y: Math.round(cy - 16 * k),
          scale: k * (hot ? 1 : 0.8),
          phase: i * 900 + (hot ? 0 : 400 + 170 * (bi % 5)),
          opacity: hot ? 0.8 : 0.5,
          cycle: { life: hot ? 2700 : 3400, rise: (hot ? 52 : 40) * k, sway: 4 * k },
        });
      }
    }
    // Lit windows that breathe. A window is the one light a painted
    // building already has, and a slow swing on it is what makes a dusk
    // town look inhabited rather than abandoned.
    if (dark || underground) {
      const lit = name === "tavern" || name === "hall" || name === "blacksmith"
        || name.startsWith("dwelling");
      if (lit && box.w * k >= 80) {
        const rx = box.w * 0.3;
        glows.push({
          x: Math.round(box.left + box.w * 0.5),
          y: Math.round(r.slot.y - box.w * a.ratio * 0.34),
          rx, ry: rx * 0.55,
          rgb: underground ? "150,120,255" : "255,190,110",
          alpha: 0.13, swing: 0.07, period: 3200 + 260 * (bi % 6), phase: 180 * bi,
        });
      }
    }
  }
  // A bank of cloud drifting across the very bottom of the frame, where a
  // floating isle or a mountain peak has no ground at all. The critic's
  // fourth high defect was a third of Prismgate's lower frame carrying
  // nothing; on this camera that band is sky, so it gets weather.
  if (FOREGROUND_CLOUD.has(faction)) {
    const s = LIFE_SPRITES["cloudbank"]!;
    for (let i = 0; i < 3; i++) {
      items.push({
        sprite: "cloudbank", layer: "over",
        x: Math.round(-120 + i * 470), y: Math.round(TOWN_CANVAS_H - s.h * 0.55),
        scale: 1 + 0.15 * i, phase: 0, opacity: 0.5 - 0.08 * i,
        drift: { vx: 7 + i * 3, vy: 0, loop: TOWN_CANVAS_W + s.w * 2 },
      });
    }
  }
  return { items, glows };
}

/** The scale a flag on a building is drawn at: the building's own. */
export const flagScale = (slot: TownSlot): number => Math.max(0.7, Math.min(1.4, bandScale(slot)));
